// ZenScroll Dynamic Daily Prompt library.
//
// 30 categories × 10 prompts = 300 unique daily prompts.
// Selection is keyed to the user's LOCAL calendar day, so the prompt flips
// at local midnight and stays constant all day. The categories are
// round-robin interleaved, which guarantees consecutive days never share a
// category (and therefore never repeat a prompt). The full library is
// exhausted before any prompt comes around again; each new cycle is offset
// by a coprime stride so the order feels fresh rather than replayed.
//
// Style rules (see PRD): uplifting, mindful, family-friendly, imaginative.

const CATEGORIES = [
  { name: 'Nature', emoji: '🌿', prompts: [
    'Create a peaceful mountain village where the trees glow at sunset.',
    'Imagine a hidden waterfall discovered deep in the jungle.',
    'Design a garden where every flower hums a gentle note in the breeze.',
    'Create a forest clearing where fireflies write constellations at dusk.',
    'Imagine a single oak tree that shelters an entire tiny world.',
    'Design a moss-covered stone bridge over a stream of liquid light.',
    'Create a meadow where the morning dew reflects tiny rainbows.',
    'Imagine a bamboo grove that sways in perfect rhythm with your breath.',
    'Design a mountain path lined with lanterns made of leaves.',
    'Create a valley where wildflowers bloom in spiral patterns.',
  ]},
  { name: 'Travel', emoji: '🌎', prompts: [
    'Imagine a magical train that travels through the clouds.',
    'Design a cozy mountain cabin you can only reach by hot-air balloon.',
    'Create a market street in a city built on gentle floating islands.',
    'Imagine the world\u2019s most peaceful airport, quiet as a library.',
    'Design a lighthouse guesthouse at the edge of a calm northern sea.',
    'Create a village where every rooftop is a small terraced garden.',
    'Imagine a slow river cruise through a canyon of amber stone.',
    'Design a desert oasis rest stop for weary star-gazing travellers.',
    'Create a postcard from a town that exists only at golden hour.',
    'Imagine wandering an old coastal town where the streets smell of citrus.',
  ]},
  { name: 'Animals', emoji: '🐾', prompts: [
    'Imagine a cat running the world\u2019s cutest coffee shop.',
    'Design a library where owls deliver the books.',
    'Create a fox curled up asleep in a bed of autumn leaves.',
    'Imagine a family of otters hosting a riverside picnic.',
    'Design a gentle giant tortoise that carries a tiny village garden.',
    'Create a penguin\u2019s dream vacation somewhere warm and safe.',
    'Imagine a golden retriever who works as a lighthouse keeper.',
    'Design a hedgehog\u2019s cozy underground reading burrow.',
    'Create a morning yoga class taught by very patient red pandas.',
    'Imagine an elephant painting a mural with its trunk at sunrise.',
  ]},
  { name: 'Food & Drinks', emoji: '🍜', prompts: [
    'Design a bakery where every dessert is inspired by flowers.',
    'Create a tiny ramen shop tucked inside a rainy alley of lanterns.',
    'Imagine a tea house floating on a lake of morning mist.',
    'Design the most inviting farmers market stall you can dream up.',
    'Create a picnic spread laid out in a field of tall summer grass.',
    'Imagine a chocolate shop run by meticulous, well-dressed bears.',
    'Design a fruit stand on a tropical island at golden hour.',
    'Create a steaming bowl of soup that glows with gentle warmth.',
    'Imagine a caf\u00E9 where each latte reveals a tiny landscape in its foam.',
    'Design a night market food street lit entirely by paper lanterns.',
  ]},
  { name: 'Fashion', emoji: '👗', prompts: [
    'Create a fashion collection inspired by the Northern Lights.',
    'Design a gown woven from morning fog and silver thread.',
    'Imagine streetwear inspired by koi ponds and water gardens.',
    'Create a scarf pattern based on your favorite season.',
    'Design cozy winter outfits for a village of snow travellers.',
    'Imagine a hat shop where every hat holds a tiny weather system.',
    'Create jewellery inspired by dewdrops on spider silk.',
    'Design a runway show set inside a blooming greenhouse.',
    'Imagine shoes designed for walking softly through dream libraries.',
    'Create a kimono pattern that tells the story of a mountain river.',
  ]},
  { name: 'Technology & AI', emoji: '💻', prompts: [
    'Design a robot that helps people practice mindfulness.',
    'Imagine a friendly household robot tending a windowsill garden.',
    'Create a computer made entirely of plants and soft light.',
    'Design the calmest workspace of the year 2100.',
    'Imagine an AI companion shaped like a small floating lantern.',
    'Create a phone-free caf\u00E9 where devices nap in tiny beds.',
    'Design a solar-powered village where technology hides in nature.',
    'Imagine a gentle robot librarian who remembers every story.',
    'Create a smartwatch face that shows your day as weather.',
    'Design a drone that plants wildflower seeds over quiet meadows.',
  ]},
  { name: 'Science & Space', emoji: '🚀', prompts: [
    'Imagine what autumn would look like on another planet.',
    'Create a greenhouse orbiting Earth, full of impossible plants.',
    'Design a cozy observation deck for watching twin sunsets.',
    'Imagine an astronaut\u2019s peaceful morning routine on the Moon.',
    'Create a telescope that shows you the dreams of distant stars.',
    'Design a gentle space station where travellers rest between worlds.',
    'Imagine rain made of stardust falling over a quiet crater lake.',
    'Create a classroom on Mars decorated by its first students.',
    'Design a comet with a tail made of glowing flower petals.',
    'Imagine a planet where the auroras sing in soft harmonies.',
  ]},
  { name: 'Art & Design', emoji: '🎨', prompts: [
    'Create a mural that makes an entire street feel calmer.',
    'Design a paint set whose colors are named after feelings.',
    'Imagine a sculpture garden where the statues slowly bloom.',
    'Create a stained-glass window telling the story of a gentle rain.',
    'Design an artist\u2019s studio built inside a converted greenhouse.',
    'Imagine a gallery where every painting is a window to somewhere real.',
    'Create abstract art from the shapes of your favorite song.',
    'Design a pottery studio on a cliff overlooking a calm sea.',
    'Imagine an origami city unfolding at sunrise.',
    'Create a watercolor world where the colors never fully dry.',
  ]},
  { name: 'Books & Literature', emoji: '📚', prompts: [
    'Create the coziest reading nook in the world.',
    'Design a bookshop that only opens during thunderstorms.',
    'Imagine a library where each book releases its own scent of place.',
    'Create a travelling bookmobile pulled by gentle giant snails.',
    'Design the cover of a book about a year of small joys.',
    'Imagine a poetry garden where verses grow on vines.',
    'Create a bedtime story landscape drifting past a child\u2019s window.',
    'Design a treehouse library connected by rope bridges.',
    'Imagine the desk of a writer who only writes happy endings.',
    'Create a map of a fictional land you\u2019d actually want to live in.',
  ]},
  { name: 'Movies & Entertainment', emoji: '🎬', prompts: [
    'Design an outdoor cinema in a meadow under the stars.',
    'Create a movie poster for the gentlest adventure ever filmed.',
    'Imagine a film set built entirely inside a snow globe.',
    'Design a puppet theatre run by a family of clever raccoons.',
    'Create the set of a cooking show hosted in a treetop kitchen.',
    'Imagine a drive-in theatre for rowboats on a calm lake.',
    'Design the costume of a superhero whose power is kindness.',
    'Create a behind-the-scenes look at a studio making dream sequences.',
    'Imagine a red carpet event where everyone wears their happiest memory.',
    'Design a tiny cinema for mice, complete with tiny popcorn.',
  ]},
  { name: 'Music', emoji: '🎵', prompts: [
    'Imagine a piano on a beach played softly by the wind.',
    'Create a concert hall inside a giant seashell.',
    'Design an instrument that turns rainfall into melodies.',
    'Imagine a choir of morning birds with tiny sheet music.',
    'Create the album cover for the sound of a Sunday morning.',
    'Design a music box city that winds up at dawn.',
    'Imagine a violin maker\u2019s workshop lit by warm evening light.',
    'Create a festival where each lantern floats up playing one note.',
    'Design headphones that let you hear how forests feel.',
    'Imagine a jazz caf\u00E9 where the smoke forms treble clefs.',
  ]},
  { name: 'History', emoji: '🏛️', prompts: [
    'Create a peaceful morning in an ancient Roman garden.',
    'Imagine a medieval market square on its happiest day of the year.',
    'Design the reading room of a great ancient library, restored.',
    'Create a scene of lantern makers in old Kyoto at dusk.',
    'Imagine the first lighthouse ever built, on its first calm night.',
    'Design a Viking ship built for exploring, not conquering.',
    'Create a scene of ancient astronomers mapping stars from a ziggurat.',
    'Imagine a Silk Road caravan resting at a starlit oasis.',
    'Design a castle kitchen preparing a harvest festival feast.',
    'Create a gentle moment of daily life in ancient Egypt at sunrise.',
  ]},
  { name: 'Architecture', emoji: '🏙️', prompts: [
    'Design a futuristic city that lives entirely underwater.',
    'Create a spiral village built around a mountain spring.',
    'Imagine a cathedral of living trees with stained-leaf windows.',
    'Design an apartment building where every balcony is a small forest.',
    'Create a train station that feels like a botanical garden.',
    'Imagine a city where all rooftops connect into one long meadow.',
    'Design a home built inside a giant geode.',
    'Create a floating monastery above a sea of clouds.',
    'Imagine a bridge designed for slow walks and long conversations.',
    'Design a tiny house that unfolds like origami at sunrise.',
  ]},
  { name: 'Fantasy Worlds', emoji: '🏖️', prompts: [
    'Create a fantasy castle built inside an ancient tree.',
    'Imagine a kingdom where the rivers flow with warm morning light.',
    'Design a wizard\u2019s cottage that grows a new room every season.',
    'Create a floating archipelago connected by rainbow ferries.',
    'Imagine a door in a hillside that opens to your calmest memory.',
    'Design a village of cloud shepherds and their fluffy flocks.',
    'Create a marketplace that only appears during a full moon.',
    'Imagine a candle-lit academy for very polite young dragons.',
    'Design a garden maze whose center holds a wishing well of stars.',
    'Create a realm where the seasons live in four neighboring valleys.',
  ]},
  { name: 'Landscapes', emoji: '🌅', prompts: [
    'Create a field of lavender stretching to a horizon of soft hills.',
    'Imagine terraced rice fields glowing at golden hour.',
    'Design a coastline where cliffs cradle tiny turquoise coves.',
    'Create rolling dunes under the biggest, calmest sky.',
    'Imagine a valley waking up under a slow-moving quilt of fog.',
    'Design an alpine lake so still it doubles the mountains.',
    'Create a savanna sunset with acacia trees in silhouette.',
    'Imagine a black-sand beach kissed by gentle silver waves.',
    'Design a canyon where morning light pours in like honey.',
    'Create the view from a hilltop after the rain has just stopped.',
  ]},
  { name: 'Vehicles', emoji: '🚗', prompts: [
    'Design a camper van built for chasing quiet sunrises.',
    'Imagine a bicycle with a basket that keeps flowers forever fresh.',
    'Create a sailboat whose sails are patterned like butterfly wings.',
    'Design a sleepy little tram that loops through a garden city.',
    'Imagine a hot-air balloon shaped like a paper lantern.',
    'Create a houseboat drifting down a river of water lilies.',
    'Design a vintage car on a coastal road, top down, no hurry.',
    'Imagine a gondola lift gliding over autumn treetops.',
    'Create a train carriage designed entirely for watching the rain.',
    'Design a rowboat built for two friends and a thermos of tea.',
  ]},
  { name: 'Sports', emoji: '⚽', prompts: [
    'Create a sunrise surf session on a glassy, gentle wave.',
    'Imagine a mountain-top yoga platform above the clouds.',
    'Design a community basketball court painted like a coral reef.',
    'Create a slow-motion moment of a perfect dive into a calm lake.',
    'Imagine a friendly village football match at golden hour.',
    'Design a climbing wall shaped like a giant blooming flower.',
    'Create an ice-skating pond lit by strings of warm lights.',
    'Imagine a morning run through a park where leaves fall like confetti.',
    'Design a peaceful archery range in a bamboo grove.',
    'Create a sailing regatta of tiny boats with rainbow sails.',
  ]},
  { name: 'Gaming', emoji: '🎮', prompts: [
    'Design the coziest video game save-point ever imagined.',
    'Create a pixel-art village where it\u2019s always harvest festival.',
    'Imagine a board game caf\u00E9 inside a converted windmill.',
    'Design the overworld map of a game about kindness.',
    'Create a retro arcade at the end of a seaside pier.',
    'Imagine a game character whose superpower is making gardens grow.',
    'Design a puzzle dungeon where every room is a tiny greenhouse.',
    'Create the loading screen of a game about slow mornings.',
    'Imagine a co-op game where two players paint the sky together.',
    'Design a gaming room in a treehouse, lit by paper stars.',
  ]},
  { name: 'Wellness & Mindfulness', emoji: '🧘', prompts: [
    'Design the ultimate peaceful workspace overlooking the ocean.',
    'Create a morning routine illustrated as a gentle garden path.',
    'Imagine a breathing exercise visualized as ocean waves.',
    'Design a meditation room suspended among treetops.',
    'Create a sanctuary where worries dissolve like morning mist.',
    'Imagine a spa day for the mind, drawn as a quiet landscape.',
    'Design a gratitude journal cover that feels like a deep breath.',
    'Create a hammock strung between two thoughts, both kind.',
    'Imagine a lighthouse whose beam is a slow, calming pulse.',
    'Design a small daily ritual that makes an ordinary room sacred.',
  ]},
  { name: 'Innovation', emoji: '💡', prompts: [
    'Design a lamp that stores sunshine for rainy days.',
    'Create an umbrella that turns rain into soft music.',
    'Imagine a mailbox that delivers letters from your future self.',
    'Design a window that always shows your favorite season.',
    'Create a bench that remembers every nice conversation it heard.',
    'Imagine a clock that measures time in moments, not minutes.',
    'Design a backpack that gets lighter when you share.',
    'Create a bridge that builds itself from acts of kindness.',
    'Imagine a vending machine that dispenses tiny encouragements.',
    'Design an alarm clock that wakes you with the smell of rain.',
  ]},
  { name: 'Emotions', emoji: '🎭', prompts: [
    'Paint what calm looks like when no one is watching.',
    'Create the color palette of a really good laugh.',
    'Imagine joy as a small animal — what does it look like?',
    'Design a landscape that feels exactly like relief.',
    'Create the moment right before a happy surprise.',
    'Imagine nostalgia as a room you can walk through.',
    'Design a weather system for the feeling of being understood.',
    'Create courage as a tiny lantern in a big gentle dark.',
    'Imagine contentment as a house — draw its front door.',
    'Design what hope looks like at five in the morning.',
  ]},
  { name: 'Seasonal Themes', emoji: '🌸', prompts: [
    'Create the very first morning of spring in a sleepy town.',
    'Imagine summer kept in a jar on a winter windowsill.',
    'Design an autumn street where leaves fall in slow motion.',
    'Create a snow day seen through a warmly lit window.',
    'Imagine the exact moment a cherry tree decides to bloom.',
    'Design a summer evening porch with fireflies and lemonade.',
    'Create a harvest scene painted in golds and soft browns.',
    'Imagine the quietest snowfall a forest has ever seen.',
    'Design a spring rain shower over a field of new umbrellas.',
    'Create the last warm day of the year, savored slowly.',
  ]},
  { name: 'Holidays', emoji: '🎉', prompts: [
    'Design a lantern festival floating over a calm harbor.',
    'Create a holiday market glowing in the first snow.',
    'Imagine a new year\u2019s morning where the whole town sleeps in.',
    'Design a birthday party for a hundred-year-old oak tree.',
    'Create a fireworks show made of bioluminescent butterflies.',
    'Imagine a gratitude feast where every dish tells a story.',
    'Design a gift-wrapping room in a cottage full of ribbon.',
    'Create a village celebration for the longest day of the year.',
    'Imagine a parade of paper boats carrying little candles.',
    'Design a quiet holiday morning, cocoa in hand, world on pause.',
  ]},
  { name: 'Photography', emoji: '📸', prompts: [
    'Capture the way light falls through a kitchen window at 7 a.m.',
    'Create a photo essay of a single street across four seasons.',
    'Imagine the world\u2019s most patient wildlife photographer\u2019s best shot.',
    'Design a darkroom where photos develop into small memories.',
    'Create a portrait of a place that raised you.',
    'Imagine a camera that only photographs quiet moments.',
    'Frame a puddle reflection that looks better than the real scene.',
    'Create a macro shot of dew turning a leaf into a chandelier.',
    'Imagine a polaroid wall of a stranger\u2019s happiest year.',
    'Capture fog rolling into a valley like a slow tide.',
  ]},
  { name: 'Hobbies & Crafts', emoji: '🎪', prompts: [
    'Design a knitting circle in a yurt during a snowstorm.',
    'Create a workbench where someone builds tiny ships in bottles.',
    'Imagine a pottery wheel spinning by a sunlit open door.',
    'Design a community garden plot shaped like a mandala.',
    'Create a scrapbook page for a perfectly ordinary Tuesday.',
    'Imagine a kite-making workshop on a windy green hill.',
    'Design a candle maker\u2019s shelf, each scent a small memory.',
    'Create a bonsai collection that mirrors a family\u2019s history.',
    'Imagine a quilt stitched from a town\u2019s favorite stories.',
    'Design a birdhouse village built by patient weekend hands.',
  ]},
  { name: 'Ocean Life', emoji: '🌊', prompts: [
    'Create an underwater meadow where seahorses graze at dawn.',
    'Imagine a whale\u2019s lullaby visualized as gentle light.',
    'Design a coral city with reef towers and anemone gardens.',
    'Create a sea turtle\u2019s hundred-year scrapbook of the ocean.',
    'Imagine tide pools that hold tiny complete worlds.',
    'Design a glass observatory on the quiet ocean floor.',
    'Create a school of fish that swims in calligraphy.',
    'Imagine an octopus who collects and arranges lost buttons.',
    'Design a moonlit swim with harmless glowing jellyfish.',
    'Create the calm blue world just beneath a gentle wave.',
  ]},
  { name: 'Insects & Wildlife', emoji: '🦋', prompts: [
    'Design the most beautiful butterfly anyone has ever seen.',
    'Create a bee\u2019s eye view of a garden in full bloom.',
    'Imagine a firefly ballroom deep in a summer wood.',
    'Design a snail\u2019s shell painted like a tiny galaxy.',
    'Create a ladybug\u2019s morning commute across a dewy leaf.',
    'Imagine a deer stepping into a clearing full of morning light.',
    'Design a dragonfly with wings like cathedral windows.',
    'Create an ant colony\u2019s underground botanical garden.',
    'Imagine a hummingbird\u2019s favorite flower, drawn from memory.',
    'Design a moth drawn not to light, but to moonflowers.',
  ]},
  { name: 'Mythical Creatures', emoji: '🐉', prompts: [
    'Imagine a tiny dragon living in a modern apartment.',
    'Create a phoenix hatchling\u2019s first gentle sunrise.',
    'Design a unicorn stable hidden in a birch forest.',
    'Imagine a griffin who delivers mail between mountain villages.',
    'Create a kind sea serpent guiding ships through fog.',
    'Design a sprite whose job is untangling wind chimes.',
    'Imagine a yeti who runs a cozy high-altitude tea house.',
    'Create a jackalope\u2019s burrow decorated for spring.',
    'Design a cloud whale that rains only on thirsty gardens.',
    'Imagine a forest guardian made of moss, antlers, and patience.',
  ]},
  { name: 'Dreamscapes', emoji: '🌌', prompts: [
    'Create a staircase of clouds leading to a floating library.',
    'Imagine a city where the streetlights are low-hanging stars.',
    'Design a bedroom that drifts gently out to sea each night.',
    'Create a field where forgotten dreams grow back as flowers.',
    'Imagine a train ride through someone\u2019s fondest memory.',
    'Design a lighthouse that guides dreamers back to morning.',
    'Create a sky where constellations rearrange into lullabies.',
    'Imagine doors floating in mist, each one slightly ajar.',
    'Design a moon garden that blooms only while you sleep.',
    'Create the bridge between today and a softer tomorrow.',
  ]},
  { name: 'Everyday Life', emoji: '☕', prompts: [
    'Create your dream beach house hidden among palm trees.',
    'Capture the small ceremony of the first coffee of the day.',
    'Design a kitchen where Sunday pancakes are a sacred ritual.',
    'Imagine a laundromat at night that feels strangely peaceful.',
    'Create a window seat made for watching the rain do its work.',
    'Design a corner store where the owner knows everyone\u2019s name.',
    'Imagine the gentle chaos of a family breakfast table.',
    'Create a balcony garden thriving on a busy city street.',
    'Design the perfect desk for writing letters by hand.',
    'Imagine an evening walk home when the whole sky turns pink.',
  ]},
];

// Round-robin interleave: entry i comes from category (i % 30), so any two
// consecutive days are guaranteed to be different categories.
export const DAILY_PROMPTS = (() => {
  const out = [];
  const depth = Math.max(...CATEGORIES.map((c) => c.prompts.length));
  for (let d = 0; d < depth; d += 1) {
    for (const cat of CATEGORIES) {
      if (cat.prompts[d]) out.push({ category: cat.name, emoji: cat.emoji, prompt: cat.prompts[d] });
    }
  }
  return out;
})();

const ANCHOR = new Date(2026, 0, 1); // local-time anchor

function localDaysSinceAnchor(date) {
  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((startOfDay - ANCHOR) / 86400000);
}

/**
 * Deterministic daily prompt, keyed to the user's LOCAL calendar date:
 * flips at local midnight, constant all day, never repeats until the full
 * 300-prompt library has been exhausted. Each full cycle is offset by a
 * stride coprime with the library size so replays arrive in a fresh order.
 */
export function getDailyPrompt(date = new Date()) {
  const n = DAILY_PROMPTS.length;
  const days = localDaysSinceAnchor(date);
  const cycle = Math.floor(days / n);
  const idx = ((days + cycle * 13) % n + n) % n;
  return { ...DAILY_PROMPTS[idx], index: idx, dayNumber: ((days % n) + n) % n + 1 };
}
